import { useQuery } from '@tanstack/react-query'

import api from '@/lib/api'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'

export interface EsocialRetornoOcorrenciaResponse {
  id: string
  codigo: string | null
  tipo: string | null
  descricao: string | null
  localizacao: string | null
  acaoSugerida: string | null
  ordem: number
  payload: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface EsocialRetornoEventoDetalhadoResponse {
  id: string
  eventoId: string
  arquivo: string | null
  statusProcessamento: string | null
  codigoRespostaProcessamento: string | null
  descricaoRespostaProcessamento: string | null
  versaoAppProcessamento: string | null
  dhProcessamento: string | null
  nrRecibo: string | null
  hashRecibo: string | null
  rubricas: unknown[] | null
  totais: Record<string, unknown>[] | null
  payload: Record<string, unknown> | null
  ocorrencias: EsocialRetornoOcorrenciaResponse[]
  createdAt: string
  updatedAt: string
}

export interface EsocialRetornoResumoResponse {
  id: string
  etapa: string
  codigoResposta: string | null
  descricaoResposta: string | null
  protocoloEnvio: string | null
  dhRecepcao: string | null
  versaoAplicativoRecepcao: string | null
  versaoAppProcessamento: string | null
  totalEventos: number
  totalOcorrencias: number
  payload: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface EsocialRetornoLoteResponse {
  id: string
  grupo: string | null
  id_previdenciario: string | null
  status: string | null
  totalEventos: number
  totalEventosProcessados: number
  totalOcorrencias: number
  totalAdvertencias: number
  totalErros: number
  protocoloEnvio: string | null
  dhRecepcao: string | null
  versaoAplicativoRecepcao: string | null
  codigoRespostaProcessamento: string | null
  descricaoRespostaProcessamento: string | null
  xmlRetornoProcessamento: string | null
  payloadProcessamento: Record<string, unknown> | null
  payloadEventoDetalhado: Record<string, unknown>[] | null
  resumos: EsocialRetornoResumoResponse[]
  eventosDetalhados: EsocialRetornoEventoDetalhadoResponse[]
  createdAt: string
  updatedAt: string
}

function toEsocialRetornoLotesArray(value: unknown): EsocialRetornoLoteResponse[] | null {
  return Array.isArray(value) ? (value as EsocialRetornoLoteResponse[]) : null
}

function normalizeEsocialRetornosResponse(payload: unknown): EsocialRetornoLoteResponse[] {
  const direct = toEsocialRetornoLotesArray(payload)
  if (direct) {
    return direct
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>
  const nestedData = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : null

  return (
    toEsocialRetornoLotesArray(record.data) ??
    toEsocialRetornoLotesArray(record.lotes) ??
    toEsocialRetornoLotesArray(nestedData?.lotes) ??
    []
  )
}

export function useFetchEsocialRetornosByPrevidenciario(idPrevidenciario?: string) {
  const token = useAccessToken()
  const user = useCurrentUser()

  return useQuery({
    queryKey: ['esocial-retornos', idPrevidenciario],
    queryFn: async (): Promise<EsocialRetornoLoteResponse[]> => {
      if (!idPrevidenciario) {
        throw new Error('ID previdenciário não informado')
      }

      if (!user || !token) {
        throw new Error('Usuário não autenticado')
      }

      const response = await api.get<EsocialRetornoLoteResponse[] | { data?: unknown; lotes?: unknown }>(`/e-social/retornos/${idPrevidenciario}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (!response.data) {
        throw new Error('Erro ao buscar status de envio do eSocial')
      }

      return normalizeEsocialRetornosResponse(response.data)
    },
    enabled: Boolean(idPrevidenciario && user && token)
  })
}
